import { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, Animated, Alert, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Trophy, Frown, Minus } from 'lucide-react-native';
import Confetti from '@/components/Confetti';

interface DuelResultBannerProps {
  isWinner: boolean;
  isLoss: boolean;
  isDraw: boolean;
  opponentName: string;
  margin?: string;
}

export default function DuelResultBanner({ isWinner, isLoss, isDraw, opponentName, margin }: DuelResultBannerProps) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const slideAnim = useRef(new Animated.Value(-60)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const glowAnim = useRef(new Animated.Value(1)).current;
  const shakeAnim = useRef(new Animated.Value(0)).current;
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    Animated.parallel([
      Animated.spring(slideAnim, { toValue: 0, useNativeDriver: true, tension: 50, friction: 8 }),
      Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }),
    ]).start(() => {
      // Post-entrance effects
      if (isWinner) {
        setShowConfetti(true);
        // First duel win celebration
        if (profile?.id) {
          AsyncStorage.getItem(`duel:first_duel_win_celebrated:${profile.id}`).then((val) => {
            if (!val) {
              AsyncStorage.setItem(`duel:first_duel_win_celebrated:${profile.id}`, '1').catch(() => {});
              const msg = '🎉 First Duel Won! You\'re a competitor now.';
              if (Platform.OS === 'web') alert(msg);
              else Alert.alert('First Victory!', msg);
            }
          }).catch(() => {});
        }
        Animated.loop(
          Animated.sequence([
            Animated.timing(glowAnim, { toValue: 1.04, duration: 600, useNativeDriver: true }),
            Animated.timing(glowAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
          ])
        ).start();
      }
      if (isLoss) {
        Animated.sequence([
          Animated.timing(shakeAnim, { toValue: 8, duration: 60, useNativeDriver: true }),
          Animated.timing(shakeAnim, { toValue: -8, duration: 60, useNativeDriver: true }),
          Animated.timing(shakeAnim, { toValue: 6, duration: 60, useNativeDriver: true }),
          Animated.timing(shakeAnim, { toValue: -6, duration: 60, useNativeDriver: true }),
          Animated.timing(shakeAnim, { toValue: 3, duration: 60, useNativeDriver: true }),
          Animated.timing(shakeAnim, { toValue: 0, duration: 60, useNativeDriver: true }),
        ]).start();
      }
    });
  }, []);

  const bgColor = isWinner
    ? theme.colors.success
    : isLoss
      ? theme.colors.error
      : '#B8860B';

  const label = isWinner ? 'VICTORY' : isLoss ? 'DEFEAT' : 'DRAW';
  const Icon = isWinner ? Trophy : isLoss ? Frown : Minus;
  const subtitle = isWinner
    ? `You beat ${opponentName}${margin ? ` by ${margin}` : ''}`
    : isLoss
      ? `${opponentName} won${margin ? ` by ${margin}` : ''}`
      : `Tied with ${opponentName}`;

  return (
    <>
    {showConfetti && <Confetti active={showConfetti} />}
    <Animated.View
      style={[
        styles.banner,
        {
          backgroundColor: bgColor,
          opacity: fadeAnim,
          transform: [
            { translateY: slideAnim },
            { translateX: shakeAnim },
            { scale: glowAnim },
          ],
        },
      ]}>
      {/* Confetti dots for winner */}
      {isWinner && (
        <>
          <View style={[styles.dot, { top: 6, left: 20, backgroundColor: '#FFD700' }]} />
          <View style={[styles.dot, { top: 12, right: 30, backgroundColor: '#FFF' }]} />
          <View style={[styles.dot, { bottom: 8, left: 50, backgroundColor: '#FFD700' }]} />
          <View style={[styles.dot, { bottom: 6, right: 60, backgroundColor: '#FFF' }]} />
        </>
      )}
      <Icon size={28} color="#FFF" />
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.subtitle}>{subtitle}</Text>
    </Animated.View>
    </>
  );
}

const styles = StyleSheet.create({
  banner: {
    borderRadius: 2,
    padding: 20,
    alignItems: 'center',
    gap: 6,
    marginBottom: 16,
    overflow: 'hidden',
  },
  label: {
    fontSize: 22,
    fontWeight: '900',
    color: '#FFF',
    letterSpacing: 3,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: '600',
    color: 'rgba(255,255,255,0.85)',
  },
  dot: {
    position: 'absolute',
    width: 6,
    height: 6,
    borderRadius: 3,
  },
});
